import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast'; 
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppSettingsModal from './AppSettingsModal';
import EmailSettings from './EmailSettings';
import AttachmentsModal from './AttachmentsModal';
import AISettingsModal from './AISettingsModal';
import { 
  Save, Search, List, ChevronRight, Filter, Plus, Paperclip, 
  Trash2, ArrowRight, ArrowLeft, Map as MapIcon, Layers, Maximize, Minimize, Building, Globe,
  Sparkles, X, Activity, Thermometer, Zap, Crosshair, Wifi, Image as ImageIcon, Menu, UploadCloud, Check
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Box } from 'lucide-react';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

import { MapContainer, TileLayer, Marker, Popup, Polygon, FeatureGroup, Tooltip, useMap, ImageOverlay } from 'react-leaflet';
import BIMViewer from './BIMViewer'; 
  
// تصليح مشكلة أيقونات Leaflet الافتراضية
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 🎯 محرك الكاميرا المُصلح (بيستقبل أمر الطيران من الـ State)
const MapViewUpdater = ({ center, zoom, flyToCoords }) => {
  const map = useMap();
  useEffect(() => {
    // الطيران السلس للأماكن اللي جاية من البحث
    if (flyToCoords) {
      map.flyTo(flyToCoords, 16, { animate: true, duration: 1.5 });
    } 
    // التركيز العادي على الأصول أو السايت
    else if (center && center[0] && center[1]) {
      map.flyTo(center, zoom, { animate: true, duration: 1.5 });
    }
  }, [center, zoom, flyToCoords, map]);
  return null;
};

// 🎯 استخراج إحداثيات الموقع وتمديدها
const getOverlayBounds = (site) => {
    const latOffset = 0.003; 
    const lngOffset = 0.005; 
    return [[site.lat - latOffset, site.lng - lngOffset], [site.lat + latOffset, site.lng + lngOffset]];
};

// 🎯 مكون لضبط الصورة على مقاس الخريطة الحالي
const ImageOverlayFitter = ({ site, isActive }) => {
  const map = useMap();
  useEffect(() => {
    if (isActive && site) {
      map.setView([site.lat, site.lng], 18, { animate: true, duration: 1.5 });
    }
  }, [isActive, site, map]);
  return null;
};

// 🎯 1. أيقونات مواقع الدار (Macro View)
const createAldarSiteIcon = (title) => {
  const html = `
    <div class="aldar-callout-container">
      <div class="aldar-callout-box">
        <div class="aldar-callout-title">${title}</div>
        <div class="aldar-callout-explore">Explore</div>
      </div>
      <div class="aldar-callout-line"></div>
      <div class="aldar-callout-dot"></div>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'bg-transparent',
    iconSize: [140, 100], 
    iconAnchor: [70, 100], 
    popupAnchor: [0, -100]
  });
};

// 🎯 2. أيقونات الـ 360 والأصول (Micro View)
const createMicroIcon = (label, type) => {
  const isAsset = type === 'ASSET';
  const colorClass = isAsset ? 'border-[#00a1df] text-[#00a1df]' : 'border-orange-500 text-orange-500';
  const html = `
    <div class="flex items-center justify-center">
      <div class="bg-white/95 backdrop-blur-sm ${colorClass} border-2 px-3 py-1 rounded-full shadow-[0_4px_10px_rgba(0,0,0,0.5)] text-[10px] font-black uppercase whitespace-nowrap transition-transform hover:scale-110">
        ${label}
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'bg-transparent',
    iconSize: [80, 30],
    iconAnchor: [40, 15],
    popupAnchor: [0, -15]
  });
};


const OrganizationDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Organization');
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen]= useState(false); 
  const [isEmailSettings, setIsEmailSettings]= useState(false);
  const [isAISettings, setIsAISettings]= useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [fieldMaps, setFieldMaps] = useState({});
  const [count, setCount] = useState(0);
  
  // 🎯 استيت الخريطة والإبهار البصري
  const [mapView, setMapView] = useState('MACRO'); 
  const [mapStyle, setMapStyle] = useState('hybrid'); 
  const [is3D, setIs3D] = useState(false); 
  const [isFullscreen, setIsFullscreen] = useState(false); 
  const mapWrapperRef = useRef(null); 
  const markerRefs = useRef({});

  // 🎯 متغيرات بحث الخريطة
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [flyToLocation, setFlyToLocation] = useState(null); // 🎯 الحل السليم للطيران

  const [selectedSiteForMap, setSelectedSiteForMap] = useState(null);
  const [spatialData, setSpatialData] = useState({ locations: [], assets: [] });
  const [focusCoords, setFocusCoords] = useState(null); 
  const [focusZoom, setFocusZoom] = useState(7);

  // استيت الـ 3D Viewer (BIM Autodesk)
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerConfig, setViewerConfig] = useState({ urn: null, dbId: null, recordId: null, bimId: null, description: null, status: null });
  
  // استيت العرض التسويقي (Virtual Tour WebGL)
  const [isVirtualTourOpen, setIsVirtualTourOpen] = useState(false);
  const [tourConfig, setTourConfig] = useState(null);

  const handleOpenViewer = (urn, recordId, bimId, description, status, meters, stats, asset_id) => {
    setViewerConfig({ urn, recordId, bimId, description, status, meters, stats, asset_id });
    setIsViewerOpen(true);
  };

  const handleOpenVirtualTour = (itemData) => {
    setTourConfig(itemData);
    setIsVirtualTourOpen(true);
  };

  const handleSliderClick = (item) => {
    if (item.lat && item.lng) {
      setFocusCoords([item.lat, item.lng]);
      setFocusZoom(17); 
      setFlyToLocation(null); // ريستارت للطيران
      const marker = markerRefs.current[item.id];
      if (marker) {
        marker.openPopup();
      }
    }
  };

  // 🎯 بحث تلقائي (بدون ضغط زرار) مع تأخير 500 ملي ثانية
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length > 2) { // البحث يبدأ بعد 3 حروف
        try {
          const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=5`);
          setSearchResults(res.data);
        } catch (error) {
          console.error("Search error:", error);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // 🎯 دالة الطيران المُصلحة (بتستخدم الـ State عشان المكون اللي جوه الخريطة يتجاوب)
  const handleFlyTo = (lat, lon, displayName) => {
      // لو اليوزر كان جوا موقع (Micro) نطلعه برا الأول عشان متبوظش الابعاد
      if (mapView === 'MICRO') {
         setMapView('MACRO');
         setSelectedSiteForMap(null);
         setIs3D(false);
      }

      // بنباصي الإحداثيات للـ MapViewUpdater
      setFlyToLocation([lat, lon]);
      setFocusCoords(null); // نفضي الـ focus القديم
      
      toast.success(`تم التوجيه بنجاح`, { icon: '🚁', position: 'bottom-center' });
      
      setSearchResults([]);
      setSearchQuery('');
  };

  const fetchSpatialData = async (site) => {
    if(!site || !site.site_id) return;
    try {
      setSelectedSiteForMap(site);
      const res = await axios.get(`/api/spatial/site/${site.site_id}`);
      setSpatialData(res.data);
      setMapView('MICRO');
      setFocusCoords([site.lat, site.lng]);
      setFlyToLocation(null); // ريستارت الطيران الحر
      setFocusZoom(17);
      if (site.image_url) {
        setIs3D(true);
      } else {
        setIs3D(false); 
      }
    } catch (err) {
      toast.error("Failed to load spatial data for this site");
    }
  };

  const [orgData, setOrgData] = useState({
    orgid: '', description: '', basecurrency1: '', basecurrency2: '',
    itemsetid: '', companysetid: '', status: 'ACTIVE', stockcategory: 'STK',
    clearingaccount: '', active: true, sites: [] 
  });

  const [lookup, setLookup] = useState({ 
    isOpen: false, title: '', type: '', domainId: '', field: '', isSiteLevel: false 
  });

  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (mapWrapperRef.current) {
        mapWrapperRef.current.requestFullscreen().catch(err => {
          toast.error("Fullscreen API is not supported on this browser.");
        });
      }
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const fetchMetadata = async () => {
        try {
            const [orgRes, siteRes] = await Promise.all([
                axios.get('/api/admin/field-map/organizations'),
                axios.get('/api/admin/field-map/sites')
            ]);
            setFieldMaps({ ...orgRes.data, ...siteRes.data });
        } catch (e) {
            console.error("Metadata Load Error");
        }
    };
    fetchMetadata();
  }, []);

  const isNew = !id || id === 'new' || id === 'undefined';
  const loadOrg = async () => {
    if (id && id !== 'new' && id !== 'undefined') {
      axios.get(`/api/organizations/${id}`).then(res => {
        const data = res.data;
        const sanitizedData = {
            ...data,
            orgid: data.org_id || data.orgid,
            sites: data.sites || [],
            ai_settings: data.ai_settings
        };
        setOrgData(sanitizedData);
        if (sanitizedData.sites.length > 0) setSelectedSiteIndex(0);
      }).catch(err => console.error("Error fetching data:", err));
    }
  }
  useEffect(() => {
    loadOrg()
    const storedUser = localStorage.getItem('user');
    const { username } = JSON.parse(storedUser);
    if (username.toLowerCase() === 'sysadmin') {
      setActiveTab("Map");
    }
  }, [id]);

  const handleSmartSearch = (fieldName, titleKey, defaultType, isSiteLevel = false) => {
      const maps = fieldMaps || {}; 
      const linkedDomain = maps[fieldName];
      
      setLookup({ 
          isOpen: true, 
          title: t(`org_detail.lookups.${titleKey}`), 
          type: linkedDomain ? 'DOMAIN' : defaultType, 
          domainId: linkedDomain || '', 
          field: fieldName,
          isSiteLevel 
      });
  };

  const fetchCount = useCallback(async () => {
    if (!orgData.orgid || isNew) {
        setCount(0);
        return;
    }
    try {
      const res = await axios.get(`/api/attachments/count/ORGANIZATION/${orgData.orgid}`);
      setCount(res.data.count || 0);
    } catch (err) { 
        console.error("Error fetching attachment count:", err); 
    }
  }, [orgData.orgid, isNew]);
  
  const handleLookupSelect = (row) => {
    const value = row.value || row.id || row.org_id || row.site_id || row.location;
    if (lookup.isSiteLevel && selectedSiteIndex !== null) {
        handleSiteDetailChange(lookup.field, value);
    } else {
        setOrgData(prev => ({ ...prev, [lookup.field]: value }));
    }
    setLookup(prev => ({ ...prev, isOpen: false }));
  };

  const handleOrgChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOrgData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSiteDetailChange = (field, value) => {
    if (selectedSiteIndex === null) return;
    const updatedSites = [...(orgData.sites || [])];
    updatedSites[selectedSiteIndex] = { ...updatedSites[selectedSiteIndex], [field]: value };
    setOrgData({ ...orgData, sites: updatedSites });
  };

  const addNewSiteRow = () => {
    const newSite = { site_id: '', description: '', active: true, state: '', contact: '', year_budget: 0, month_budget: 0, image_url: '' };
    const currentSites = orgData.sites || [];
    const updatedSites = [...currentSites, newSite];
    setOrgData({ ...orgData, sites: updatedSites });
    setSelectedSiteIndex(updatedSites.length - 1); 
  };

  const removeSiteRow = (idx, e) => {
    e.stopPropagation();
    const updatedSites = (orgData.sites || []).filter((_, i) => i !== idx);
    setOrgData({ ...orgData, sites: updatedSites });
    if (selectedSiteIndex === idx) setSelectedSiteIndex(null);
  };

  const handleSave = useCallback(async () => {
    const currentOrgId = orgData.orgid || orgData.org_id;
    if (!currentOrgId) return toast.error(t('org_detail.toasts.id_required'));

    const loadingToast = toast.loading(t('org_detail.toasts.saving'));
    try {
      const payload = {
          ...orgData,
          orgid: currentOrgId,
          sites: orgData.sites || []
      };
      await axios.post('/api/organizations/save', payload);
      toast.dismiss(loadingToast);
      toast.success(t('org_detail.toasts.success'));
      
      if (id === 'new') navigate(`/organizations/${currentOrgId}`);
    } catch (err) {
      toast.dismiss(loadingToast);
      const errorDetail = err.response?.data?.detail || t('org_detail.toasts.fail');
      toast.error(errorDetail, { duration: 5000 });
    }
  }, [orgData, id, navigate, t]);
  
  const handleSiteImageUpload = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file (PNG, JPG)");
      return;
    }

    const currentSite = orgData.sites[idx];
    if (!currentSite.site_id) {
      toast.error("Please set a Site ID first.");
      return;
    }

    const loadId = toast.loading("Uploading 3D Map image...");
    try {
      const tempUrl = URL.createObjectURL(file);
      const updatedSites = [...orgData.sites];
      updatedSites[idx] = { ...updatedSites[idx], image_url: tempUrl };
      setOrgData({ ...orgData, sites: updatedSites });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', currentSite.site_id);

      const res = await axios.post('/api/sites/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      updatedSites[idx].image_url = res.data.url;
      setOrgData({ ...orgData, sites: updatedSites });
      
      toast.success("3D Map Image uploaded successfully", { id: loadId });
    } catch (err) {
      toast.error("Upload failed", { id: loadId });
    }
  };

  const handleMapCreate = (e) => {
    if (!selectedSiteForMap) {
      toast.error(t('org.messages.selectSiteToDraw', 'Please drill down into a Site before drawing.'));
      e.layer.remove();
      return;
    }

    const layer = e.layer;
    const geoJsonData = layer.toGeoJSON();
    const coords = geoJsonData.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);

    const centerLat = coords[0][0]; 
    const centerLng = coords[0][1];

    const updatedSites = [...orgData.sites];
    const siteIndex = updatedSites.findIndex(s => s.site_id === selectedSiteForMap.site_id);
    
    if (siteIndex !== -1) {
      updatedSites[siteIndex].geojson_boundary = JSON.stringify(coords);
      updatedSites[siteIndex].lat = centerLat;
      updatedSites[siteIndex].lng = centerLng;
      
      setOrgData({ ...orgData, sites: updatedSites });
      toast.success(`Boundary drawn and saved for site ${selectedSiteForMap.site_id}`);
    }
  };

  const handleMapEdit = (e) => {
  };
  
  const handleSaveAiSettings = async (settingsData) => {
    const currentOrgId = orgData.orgid || orgData.org_id;
    if (!currentOrgId) return toast.error(t('org_detail.toasts.id_required'));

    try {
      const payload = {
        ...settingsData,
        confidence_threshold: Number(settingsData.confidence_threshold),
        max_tasks_count: Number(settingsData.max_tasks_count)
      };

      const response = await axios.post(`/api/organizations/${currentOrgId}/ai-settings`, payload);

      if (response.data.status === 'success') {
        setOrgData(prev => ({
          ...prev,
          ai_settings: settingsData
        }));

        toast.success(t('toasts.save_success'));
        setIsAISettings(false);
      }
    } catch (err) {
      console.error("Error saving AI settings:", err);
      const msg = err.response?.data?.detail || t('toasts.load_fail');
      toast.error(msg);
    }
  };

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);

  useEffect(() => {
    const AddModalOpen = () => setIsSettingsModalOpen(true);
    window.addEventListener('OPEN_ORG_SETTINGS', AddModalOpen);
    return () => window.removeEventListener('OPEN_ORG_SETTINGS', AddModalOpen);
  }, []);

  useEffect(() => {
    const AddEmailModalOpen = () => setIsEmailSettings(true);
    window.addEventListener('OPEN_EMAIL_SETTINGS', AddEmailModalOpen);
    return () => window.removeEventListener('OPEN_EMAIL_SETTINGS', AddEmailModalOpen);
  }, []);
  
  useEffect(() => {
    const AddAIModalOpen = () => setIsAISettings(true);
    window.addEventListener('OPEN_AI_SETTINGS', AddAIModalOpen);
    return () => window.removeEventListener('OPEN_AI_SETTINGS', AddAIModalOpen);
  }, []);
  
  useEffect(() => {
    fetchCount();
  }, [orgData.orgid, fetchCount]);
  
  const currentAssetStatuses = React.useMemo(() => {
    const statusesArray = []; 
    if (spatialData.assets) {
      spatialData.assets.forEach(ast => {
        if (ast.bim_id && ast.status) {
          statusesArray.push({ guid: ast.bim_id, status: ast.status });
        }
      });
    }
    return statusesArray;
  }, [spatialData]);

  const getSiteImage = (id) => `https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=400&h=200&sig=${id}`;

  const combinedSliderItems = React.useMemo(() => {
    if (mapView !== 'MICRO') return [];
    return [
      ...(spatialData.locations || []).map(l => ({...l, type: 'LOCATION', id: l.location_id, name: l.location_id, desc: l.description})),
      ...(spatialData.assets || []).map(a => ({...a, type: 'ASSET', id: a.assetnum, name: a.assetnum, desc: a.description}))
    ];
  }, [spatialData, mapView]);


  const has3DImage = mapView === 'MICRO' && selectedSiteForMap?.image_url;
  const applyCssTilt = is3D && !has3DImage; 

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'} text-[11px]`}>
      <UniversalLookup 
        {...lookup}
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={handleLookupSelect} 
      />
      <AttachmentsModal 
        isOpen={isDocModalOpen} 
        onClose={() => { setIsDocModalOpen(false); fetchCount(); }} 
        ownertable="ORGANIZATION" 
        ownerid={orgData.orgid}
      />
      <AppSettingsModal 
        isOpen={isSettingsModalOpen} 
        onClose={() => { setIsSettingsModalOpen(false); }} 
        orgId={orgData.orgid}
      />     
      <EmailSettings 
        isOpen={isEmailSettings} 
        onClose={() => { setIsEmailSettings(false); }} 
        orgId={orgData.orgid}
      />  
      <AISettingsModal 
        key={orgData.orgid + (orgData.ai_settings ? 'set' : 'unset')}
        isOpen={isAISettings}
        orgId={orgData.orgid}
        ai_provider={orgData?.ai_provider}
        api_key={orgData?.api_key}
        currentSettings={orgData.ai_settings}
        onClose={() => setIsAISettings(false)}
        onSave={handleSaveAiSettings}
      />  

      {/* ================= 1. BIM VIEWER MODAL ================= */}
      {isViewerOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col animate-in fade-in duration-300">
          <div className="bg-[#0f172a] text-white p-3 flex justify-between items-center shadow-lg border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-sm"><Box size={18} /></div>
              <div>
                <h3 className="font-black tracking-widest uppercase text-sm">{t('loc.viewer.title', 'Engineering Digital Twin (BIM)')}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase">{viewerConfig.recordId}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsViewerOpen(false)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-sm font-black uppercase text-[10px] transition-colors"
            >
              {t('common.close', 'Close')}
            </button>
          </div>
          <div className="flex-1 relative">
             <BIMViewer 
               urn={viewerConfig.urn} 
               isActiveTab={isViewerOpen}
               assetStatuses={currentAssetStatuses} 
               activeGuidObj={viewerConfig.bimId ? { 
                   guid: viewerConfig.bimId, 
                   assetnum: viewerConfig.recordId,
                   description: viewerConfig.description,
                   status: viewerConfig.status,
                   meters: viewerConfig.meters,
                   stats: viewerConfig.stats,
                   asset_id: viewerConfig.asset_id 
               } : null} 
             />
          </div>
        </div>
      )}

      {/* ================= 2. VIRTUAL TOUR MODAL ================= */}
      {isVirtualTourOpen && (
        <div className="fixed inset-0 z-[99999] bg-[#050505] flex flex-col animate-in fade-in duration-700 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-10 pointer-events-none">
            <div className="bg-black/30 backdrop-blur-xl border border-white/10 p-4 rounded-2xl pointer-events-auto shadow-2xl flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-orange-400 to-red-600 rounded-xl shadow-[0_0_20px_rgba(249,115,22,0.4)]">
                <Sparkles className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-white text-xl font-black uppercase tracking-widest leading-none drop-shadow-md">
                  Virtual Twin Experience
                </h2>
                <p className="text-orange-300 text-xs font-black tracking-[0.2em] mt-1 uppercase">
                  {tourConfig?.location_id || tourConfig?.assetnum || "Facility Preview"}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setIsVirtualTourOpen(false)} 
              className="bg-white/10 hover:bg-red-600 backdrop-blur-md text-white p-3 rounded-xl pointer-events-auto transition-all shadow-xl border border-white/20 hover:border-red-500"
            >
              <X size={24} />
            </button>
          </div>

          <div className="absolute bottom-10 left-10 z-10 pointer-events-none flex flex-col gap-4">
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 p-4 rounded-2xl w-64 shadow-2xl pointer-events-auto transform hover:scale-105 transition-transform">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-400 font-bold text-[10px] tracking-widest uppercase">System Status</span>
                <span className="flex items-center gap-1 text-green-400 text-[10px] font-black bg-green-400/10 px-2 py-1 rounded-full animate-pulse">
                  <Activity size={12}/> ONLINE
                </span>
              </div>
              <div className="flex items-end gap-3 border-b border-white/10 pb-3 mb-3">
                <Thermometer className="text-blue-400" size={24} />
                <div>
                  <div className="text-white font-black text-2xl leading-none">22.4°C</div>
                  <div className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">Ambient Temp</div>
                </div>
              </div>
              <div className="flex items-end gap-3">
                <Zap className="text-yellow-400" size={24} />
                <div>
                  <div className="text-white font-black text-2xl leading-none">145<span className="text-sm">kW</span></div>
                  <div className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">Current Power Load</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 w-full h-full relative">
             <iframe 
               title="3D Model Virtual Tour" 
               src="https://sketchfab.com/models/16ed96dee1e04f7a8e1c80c956669b94/embed?autostart=1&camera=0&ui_controls=1&ui_infos=0&ui_inspector=0&ui_stop=0&ui_watermark=0&ui_hint=2&ui_theme=dark&dnt=1&annotations_visible=1" 
               className="w-full h-full border-0" 
               allow="autoplay; fullscreen; xr-spatial-tracking" 
             />
             <div className="absolute bottom-0 right-0 w-full h-12 bg-[#050505] pointer-events-none"></div>
          </div>
        </div>
      )}


      {/* Header Info */}
      <div className={`bg-white p-2 border-b border-gray-300 flex items-center gap-10 shadow-sm `}>
        <div className={`flex items-center gap-2 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <label className="text-gray-500 font-bold uppercase tracking-tighter">{t('org_detail.org_label')}</label>
            <input name="orgid" className="border-b border-dashed border-gray-400 w-24 outline-none font-bold text-blue-900 bg-transparent uppercase" 
                   value={orgData.orgid || ''} onChange={handleOrgChange} disabled={!isNew} />
            <div className="relative flex items-center">
               <input name="description" className={`border-b border-gray-400 w-[550px] outline-none text-blue-800 font-bold bg-transparent ${isRTL ? 'pl-6 text-right' : 'pr-6'}`} 
                      value={orgData.description || ''} onChange={handleOrgChange} />
               <List size={14} className={`text-blue-700 cursor-pointer absolute ${isRTL ? 'left-0' : 'right-0'}`} />
            </div>
            {!isNew && <button 
              onClick={() => setIsDocModalOpen(true)}
              className="relative p-2 bg-blue-50 text-blue-900 rounded-sm hover:bg-blue-600 hover:text-white transition-all shadow-sm group"
            >
              <Paperclip size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black border border-white">
                  {count}
                </span>
              )}
            </button>}            
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex bg-[#3c70a4] text-white shadow-md`}>
        {['Organization', 'Sites', 'Map'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-8 py-2 font-bold border-r border-white/10 transition-all
            ${activeTab === tab ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10'}`}>
            {t(`org_detail.tabs.${tab.toLowerCase()}`)}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar text-[11px]">
        
        {/* ================= ORGANIZATION TAB ================= */}
        {activeTab === 'Organization' && (
          <div className="bg-white p-12 border border-gray-300 shadow-sm max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500 " dir={isRTL ? 'rtl' : 'ltr'}>
             <LookupField label={t('org_detail.fields.currency1')} name="basecurrency1" value={orgData.basecurrency1} desc={t('org_detail.descs.currency1')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('basecurrency1', 'currency', 'DOMAIN')} />
             <LookupField label={t('org_detail.fields.currency2')} name="basecurrency2" value={orgData.basecurrency2} desc={t('org_detail.descs.currency2')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('basecurrency2', 'currency', 'DOMAIN')} />
             <div className="h-4 border-b border-gray-100" />
             <LookupField label={t('org_detail.fields.item_set')} name="itemsetid" value={orgData.itemsetid} desc={t('org_detail.descs.item_set')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('itemsetid', 'item_set', 'itemsets')} />
             <LookupField label={t('org_detail.fields.company_set')} name="companysetid" value={orgData.companysetid} desc={t('org_detail.descs.company_set')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('companysetid', 'company_set', 'companysets')} />
             <SearchField label={t('org_detail.fields.item_status')} name="status" value={orgData.status} isRequired 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('status', 'status', 'DOMAIN')} />
             <div className={`flex items-center gap-4 mt-8 `}>
                <label className="w-48 text-right font-bold text-gray-500 uppercase tracking-tighter">{t('org_detail.fields.active')}</label>
                <input type="checkbox" name="active" checked={orgData.active || false} onChange={handleOrgChange} className="w-4 h-4" />
             </div>
          </div>
        )}

        {/* ================= SITES TAB ================= */}
        {activeTab === 'Sites' && (
          <div className="flex flex-col gap-4 animate-in slide-in-from-right-5 duration-300">
            <div className="bg-white border border-gray-300 shadow-sm overflow-hidden">
              <div className={`p-2 bg-gray-100 border-b flex justify-between items-center text-gray-600 font-bold `}>
                <div className={`flex items-center gap-6 `}>
                  <span className="text-[11px] text-gray-800 uppercase tracking-widest">{t('org_detail.sites.management')}</span>
                  <button onClick={addNewSiteRow} className="flex items-center gap-1 border border-blue-400 px-3 py-0.5 bg-white text-blue-700 font-black hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                    <Plus size={12}/> {t('org_detail.sites.new_row')}
                  </button>
                </div>
                <div className={`text-blue-800 flex items-center gap-2 `}>
                   <ChevronRight size={16} className={`${isRTL ? '' : 'rotate-180'} cursor-pointer`} onClick={() => setSelectedSiteIndex(prev => Math.max(0, prev - 1))} /> 
                   <span className="tracking-widest">{orgData.sites?.length > 0 ? `${selectedSiteIndex + 1} ${t('org_detail.sites.of')} ${orgData.sites.length}` : `0 ${t('org_detail.sites.of')} 0`}</span> 
                   <ChevronRight size={16} className={`${isRTL ? 'rotate-180' : ''} cursor-pointer`} onClick={() => setSelectedSiteIndex(prev => Math.min(orgData.sites.length - 1, prev + 1))} />
                </div>
              </div>
              <table className="w-full border-collapse text-[11px]">
                <thead className="bg-[#f8f9fa] border-b text-gray-500 font-black uppercase tracking-tighter">
                  <tr className={``}>
                    <th className="p-2 border-r w-8"></th>
                    <th className={`p-2 border-r w-48 `}>{t('org_detail.sites.site_id')}</th>
                    <th className={`p-2 border-r `}>{t('org_detail.sites.description')}</th>
                    <th className="p-2 border-r text-center w-20">{t('org_detail.sites.active')}</th>
                    <th className="p-2 w-10 text-center">{t('org_detail.sites.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgData.sites?.map((site, idx) => (
                    <tr key={idx} onClick={() => setSelectedSiteIndex(idx)}
                        className={`border-b transition-colors cursor-pointer  ${selectedSiteIndex === idx ? 'bg-[#e0efff]' : 'hover:bg-gray-50'}`}>
                      <td className="p-2 border-r text-center"><ChevronRight size={14} className={`${selectedSiteIndex === idx ? 'text-blue-600' : 'text-gray-300'} ${isRTL ? 'rotate-180' : ''}`}/></td>
                      <td className="p-2 border-r">
                         <input className={`bg-transparent outline-none w-full font-black uppercase text-blue-900 `} 
                                value={site.site_id || site.siteid || ''} 
                                onChange={(e) => handleSiteDetailChange('site_id', e.target.value.toUpperCase())} />
                      </td>
                      <td className="p-2 border-r">
                         <input className={`bg-transparent outline-none w-full font-bold `} value={site.description || ''} 
                                onChange={(e) => handleSiteDetailChange('description', e.target.value)} />
                      </td>
                      <td className="p-2 border-r text-center">
                        <input type="checkbox" checked={site.active} onChange={(e) => handleSiteDetailChange('active', e.target.checked)} />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => removeSiteRow(idx, e)}>
                        <Trash2 size={12} className="text-red-400 hover:text-red-700 mx-auto transition-colors"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedSiteIndex !== null && orgData.sites[selectedSiteIndex] && (
              <div className="space-y-4 animate-in slide-up duration-300">
                <div className="bg-white border border-gray-300 shadow-md">
                  <div className={`p-1.5 bg-[#4a5568] text-white font-black uppercase tracking-widest text-[9px] `}>
                    {t('org_detail.sites.site_details')}: {orgData.sites[selectedSiteIndex].site_id}
                  </div>
                  <div className={`p-6 grid grid-cols-2 gap-x-20 gap-y-6 `}>
                     <DetailField label={t('org_detail.sites.state')} value={orgData.sites[selectedSiteIndex].state} hasSearch isRTL={isRTL}
                                  onSearch={() => handleSmartSearch('state', 'governorate', 'DOMAIN', true)} />
                     
                     <DetailField label={t('org_detail.sites.contact')} value={orgData.sites[selectedSiteIndex].contact} hasList isRTL={isRTL}
                                  onChange={(v) => handleSiteDetailChange('contact', v)} />
                  </div>
                </div>

                <div className="bg-white border border-gray-300 shadow-sm">
                  <div className={`p-1.5 bg-[#f3f3f3] border-b font-bold text-gray-600 uppercase tracking-tighter text-[9px] `}>
                    {t('org_detail.sites.fin_info')}
                  </div>
                  <div className={`p-6 grid grid-cols-2 gap-20 `}>
                     <div className="space-y-4">
                        <div className="border-b border-gray-200">
                           <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('org_detail.sites.yearly_budget')}</label>
                           <input type="number" className={`w-full bg-transparent outline-none font-bold text-blue-900 pb-1 `} 
                                 value={orgData.sites[selectedSiteIndex].year_budget || 0}
                                 onChange={(e) => handleSiteDetailChange('year_budget', e.target.value)} />
                        </div>
                        <div className="border-b border-gray-200">
                           <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('org_detail.sites.monthly_budget')}</label>
                           <input type="number" className={`w-full bg-transparent outline-none font-bold text-blue-900 pb-1 `} 
                                 value={orgData.sites[selectedSiteIndex].month_budget || 0}
                                 onChange={(e) => handleSiteDetailChange('month_budget', e.target.value)} />
                        </div>
                     </div>
                     <div className="flex flex-col gap-4">
                       {/* 🎯 إضافة حقل رفع صورة الـ 3D للموقع */}
                       <div className="border-b border-gray-200 focus-within:border-blue-500 transition-all flex flex-col justify-end pb-1">
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">3D Map Overlay</label>
                          <div className="flex items-center justify-between">
                            {orgData.sites[selectedSiteIndex].image_url ? (
                              <span className="text-green-600 text-[10px] font-black flex items-center gap-1"><Check size={12}/> Uploaded</span>
                            ) : (
                              <span className="text-gray-400 text-[10px] italic">No image</span>
                            )}
                            <label className="cursor-pointer bg-blue-50 text-blue-700 px-3 py-1 rounded text-[9px] font-black uppercase hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-1">
                              <UploadCloud size={12}/> Upload
                              <input type="file" className="hidden" accept="image/*" onChange={(e) => handleSiteImageUpload(e, selectedSiteIndex)} />
                            </label>
                          </div>
                       </div>
                       
                       <div className={`flex flex-col justify-center bg-green-50/50 p-4 border-green-500 rounded-sm ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`}>
                          <label className="text-[9px] font-bold text-green-700 uppercase">{t('org_detail.sites.utilization')}</label>
                          <span className="text-lg font-black text-green-900">100.00 %</span>
                          <p className="text-[9px] text-green-600 italic">{t('org_detail.sites.fin_health')}</p>
                       </div>
                     </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* ================= MAP TAB ================= */}
        {activeTab === 'Map' && (
        <div ref={mapWrapperRef} className={`relative flex flex-col bg-[#111111] overflow-hidden transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-[9999] h-screen w-screen' : 'w-full mx-auto h-[750px] border border-gray-800 rounded-xl shadow-2xl'}`}>
          
          {/* 🎯 Header Controls */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-[1000] pointer-events-none">
            <div className="flex items-center gap-3 bg-black/60 backdrop-blur-md p-2 px-4 rounded-full border border-white/10 pointer-events-auto shadow-lg">
              <div className="bg-orange-500 p-1.5 rounded-full text-white">
                <Globe size={16} />
              </div>
              <h3 className="font-black uppercase tracking-widest text-white text-xs">
                {mapView === 'MACRO' ? t('org.mapView', 'Global Facilities') : `Facility: ${selectedSiteForMap?.site_id}`}
              </h3>
            </div>
            
            <div className="flex items-center gap-3 pointer-events-auto">
              {/* Map Style Toggle */}
              <div className="flex items-center bg-black/60 backdrop-blur-md p-1 rounded-full border border-white/10 shadow-lg">
                <button onClick={() => setMapStyle('street')} className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase transition-all ${mapStyle === 'street' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>Street</button>
                <button onClick={() => setMapStyle('satellite')} className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase transition-all ${mapStyle === 'satellite' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>Satellite</button>
                <button onClick={() => setMapStyle('hybrid')} className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase transition-all ${mapStyle === 'hybrid' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}>Hybrid</button>
              </div>

              {/* 🎯 3D Tilt / Image Overlay Toggle */}
              {((mapView === 'MACRO') || (mapView === 'MICRO' && selectedSiteForMap?.image_url)) && (
                <button 
                  onClick={() => setIs3D(!is3D)} 
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] font-black uppercase transition-all shadow-lg border ${is3D ? 'bg-orange-500 text-white border-orange-400' : 'bg-black/60 backdrop-blur-md text-white border-white/10 hover:bg-black/80'}`}
                >
                  {mapView === 'MICRO' ? (
                    <><ImageIcon size={14} /> {is3D ? 'Exit 3D Overlay' : 'Launch 3D Overlay'}</>
                  ) : (
                    <><Building size={14} /> 3D Tilt</>
                  )}
                </button>
              )}

              {/* Draw Boundary Dropdown */}
              <div className="flex items-center gap-2 bg-blue-50/50 px-3 py-1.5 rounded-md border border-blue-100">
                <label className="text-[10px] font-black uppercase text-blue-800 tracking-tighter">
                  Draw Boundary:
                </label>
                <select 
                  className="bg-white border border-blue-200 text-blue-900 text-[10px] font-bold py-1 px-2 rounded-md outline-none cursor-pointer min-w-[120px] shadow-sm"
                  value={selectedSiteForMap?.site_id || ''}
                  onChange={(e) => {
                    const site = orgData.sites.find(s => s.site_id === e.target.value);
                    setSelectedSiteForMap(site);
                    setMapView('MACRO'); 
                  }}
                >
                  <option value="">-- SELECT --</option>
                  {orgData.sites?.map((s, idx) => (
                    <option key={idx} value={s.site_id}>{s.site_id}</option>
                  ))}
                </select>
              </div>

              {mapView === 'MICRO' && (
                <button 
                  onClick={() => {
                    setMapView('MACRO');
                    setSelectedSiteForMap(null); 
                    setFocusZoom(7);
                    setFocusCoords(null);
                    setFlyToLocation(null);
                    setIs3D(false); // 🎯 تأكيد الخروج من وضع الـ 3D عند العودة للخريطة العالمية
                  }}
                  className="bg-black/60 backdrop-blur-md hover:bg-black/80 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase flex items-center gap-2 shadow-lg border border-white/10 transition-all"
                >
                  <ArrowLeft size={14}/> Back to World
                </button>
              )}

              {/* Fullscreen */}
              <button 
                onClick={toggleFullscreen} 
                className="bg-black/60 backdrop-blur-md hover:bg-black/80 border border-white/10 text-white p-2 rounded-full shadow-lg transition-colors"
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={14}/> : <Maximize size={14}/>}
              </button>
            </div>
          </div>
          
          {/* 🎯 Sidebar (Micro View Aldar Style) */}
          {mapView === 'MICRO' && selectedSiteForMap && (
            <div className="absolute top-20 left-4 w-72 bg-[#121212]/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-[1000] overflow-hidden animate-in slide-in-from-left-8 duration-500">
               <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-gray-400 text-[9px] font-bold uppercase tracking-widest">Selected Facility</div>
                    <h2 className="text-white text-lg font-black uppercase tracking-wider">{selectedSiteForMap.site_id}</h2>
                  </div>
                  <button onClick={() => { setMapView('MACRO'); setIs3D(false); }} className="bg-white/10 p-1.5 rounded-md hover:bg-white/20 text-white transition-colors"><ArrowLeft size={16}/></button>
               </div>
               
               <div className="relative h-40 w-full bg-gray-800">
                 {/* لو في صورة مرفوعة، هنعرضها هنا في الشريط الجانبي كمان */}
                 <img src={selectedSiteForMap.image_url || getSiteImage(selectedSiteForMap.site_id)} alt="site" className="w-full h-full object-cover" />
                 <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent"></div>
               </div>
               
               <div className="p-5 space-y-4">
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">Total Assets</span>
                    <span className="text-white font-black">{spatialData.assets?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">Locations</span>
                    <span className="text-white font-black">{spatialData.locations?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">Active WOs</span>
                    <span className="text-orange-400 font-black">12</span>
                  </div>
                  
                  
               </div>
            </div>
          )}

          <div className="flex-1 w-full bg-[#050505] relative" style={{ perspective: '1200px' }}>
            <div style={{
               height: '100%', width: '100%',
               transform: applyCssTilt ? 'rotateX(55deg) scale(1.3) translateY(10%)' : 'none',
               transformOrigin: 'bottom center',
               transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
            {/* حاوية الخريطة بالكامل */}
            <div className="relative w-full h-full overflow-hidden shadow-sm border border-slate-200">
              
              {/* 🔍 مربع البحث العايم فوق الخريطة */}
              <div className="absolute top-4 left-1/3 transform -translate-x-1/2 z-[1000] w-[400px] flex flex-col gap-1">
                <div className="flex bg-white/90 backdrop-blur-sm shadow-lg rounded-lg border border-slate-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث عن مدينة، شارع، أو محطة..." 
                    className="flex-1 px-4 py-3 outline-none bg-transparent font-bold text-slate-700 text-sm"
                    dir="auto"
                  />
                </div>

                {/* 📋 قائمة نتائج البحث */}
                {searchResults.length > 0 && (
                  <div className="bg-white/95 backdrop-blur-md shadow-xl rounded-lg border border-slate-200 max-h-[250px] overflow-y-auto animate-in slide-in-from-top-2 custom-scrollbar">
                    {searchResults.map((result, idx) => (
                      <div 
                        key={idx}
                        onClick={() => handleFlyTo(result.lat, result.lon, result.display_name)}
                        className="flex items-start gap-3 p-3 border-b border-slate-100 last:border-0 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <MapPin size={16} className="text-orange-500 mt-1 shrink-0" />
                        <span className="text-xs font-bold text-slate-700 text-right leading-relaxed line-clamp-2 w-full" dir="rtl">
                          {result.display_name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 🎯 مكون الخريطة */}
              <MapContainer 
                center={focusCoords || (mapView === 'MICRO' && selectedSiteForMap?.lat ? [selectedSiteForMap.lat, selectedSiteForMap.lng] : [30.0444, 31.2357])}
                zoom={focusZoom || (mapView === 'MICRO' ? 17 : 6)} 
                className="h-full w-full custom-map-tiles"
                zoomControl={false} 
              >
                {/* 🎯 المحرك الجديد اللي بياخد اوامر الطيران */}
                <MapViewUpdater 
                   center={focusCoords || (mapView === 'MICRO' && selectedSiteForMap?.lat ? [selectedSiteForMap.lat, selectedSiteForMap.lng] : [30.0444, 31.2357])} 
                   zoom={focusZoom || (mapView === 'MICRO' ? 17 : 6)} 
                   flyToCoords={flyToLocation}
                />

                {mapStyle === 'street' && (
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                )}
                
                {(mapStyle === 'satellite' || mapStyle === 'hybrid') && (
                  <TileLayer 
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" 
                    attribution="Tiles &copy; Esri"
                  />
                )}

                {mapStyle === 'hybrid' && (
                  <TileLayer 
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution="Labels &copy; Esri"
                  />
                )}

                {/* 🎯 الإبهار: فرد الصورة */}
                {is3D && mapView === 'MICRO' && selectedSiteForMap?.image_url && (
                  <>
                    <ImageOverlayFitter site={selectedSiteForMap} isActive={is3D} />
                    <ImageOverlay 
                       url={selectedSiteForMap.image_url} 
                       bounds={getOverlayBounds(selectedSiteForMap)} 
                       zIndex={1000} /* 🎯 ضروري يكون أكبر من الماركرز عشان يغطي الخريطة */
                       opacity={1}
                    />
                  </>
                )}

                {!is3D && (
                  <FeatureGroup>
                    <EditControl
                      position="topleft"
                      onCreated={handleMapCreate}
                      onEdited={handleMapEdit}
                      onDeleted={() => {  }}
                      draw={{
                        polygon: { shapeOptions: { color: '#f97316', fillColor: '#f97316', fillOpacity: 0.2, weight: 3 }, allowIntersection: false },
                        rectangle: false, circle: false, circlemarker: false, marker: false, polyline: false,
                      }}
                    />
                  </FeatureGroup>
                )}
        
                {/* ----------------- MACRO VIEW (دبابيس موقع الدار) ----------------- */}
                {mapView === 'MACRO' && orgData.sites?.map((site, index) => {
                  if (!site.lat || !site.lng) return null;
                  return (
                    <React.Fragment key={index}>
                      {site.geojson_boundary && (
                        <Polygon positions={JSON.parse(site.geojson_boundary)} pathOptions={{ color: '#ff4500', fillColor: '#ff4500', fillOpacity: 0.35, weight: 4 }} >
                        </Polygon>
                      )}
                      <Marker 
                         position={[site.lat, site.lng]} 
                         icon={createAldarSiteIcon(site.description || site.site_id)}
                         eventHandlers={{
                           click: () => fetchSpatialData(site)
                         }}
                      />
                    </React.Fragment>
                  );
                })}
        
                {/* ----------------- MICRO VIEW (الأصول واللوكيشنز) ----------------- */}
                {mapView === 'MICRO' && (
                  <>
                    {spatialData.locations?.map((loc, idx) => (
                      <Marker 
                        key={`loc-${idx}`} 
                        position={[loc.lat, loc.lng]} 
                        icon={createMicroIcon(loc.location_id, 'LOCATION')}
                        eventHandlers={{
                          add: (e) => { markerRefs.current[loc.location_id] = e.target; }
                        }}
                      >
                        <Popup className="glass-popup" closeButton={false} offset={[0, -10]}>
                          <div className="p-4 text-center min-w-[200px]">
                            <h4 className="font-black text-white uppercase text-[16px] leading-tight mb-1 drop-shadow-md">{loc.location_id}</h4>
                            <p className="text-[11px] text-gray-300 font-bold uppercase tracking-widest mb-4">{loc.description}</p>
                            
                            <div className="flex flex-col gap-2">
                              <button 
                                onClick={() => handleOpenVirtualTour(loc)}
                                className="bg-orange-600 hover:bg-orange-500 text-white px-3 py-2 rounded-full text-[11px] font-black uppercase flex items-center gap-2 justify-center transition-all shadow-[0_0_15px_rgba(249,115,22,0.5)] w-full"
                              >
                                <Sparkles size={14}/> Virtual Tour
                              </button>

                              {loc.model_urn && loc.bim_id && (
                                <button 
                                  onClick={() => handleOpenViewer(loc.model_urn, loc.location_id, loc.bim_id, loc.description)}
                                  className="bg-white text-[#1a3353] px-3 py-2 rounded-full text-[11px] font-black uppercase flex items-center gap-2 justify-center transition-all w-full shadow-lg"
                                >
                                  <Box size={14}/> Digital Twin (BIM)
                                </button>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
        
                    {spatialData.assets?.map((ast, idx) => (
                      <Marker 
                          key={`ast-${idx}`} 
                          position={[ast.lat, ast.lng]} 
                          icon={createMicroIcon(ast.assetnum, 'ASSET')}
                          eventHandlers={{
                            add: (e) => { markerRefs.current[ast.assetnum] = e.target; }
                          }}
                        >
                        <Popup className="glass-popup" closeButton={false} offset={[0, -10]}>
                          <div className="p-4 text-center min-w-[200px]">
                            <h4 className="font-black text-white uppercase text-[16px] leading-tight mb-1 drop-shadow-md">{ast.assetnum}</h4>
                            <p className="text-[11px] text-gray-300 font-bold uppercase tracking-widest mb-4">{ast.description}</p>
                            
                            <div className="flex flex-col gap-2">
                              <button 
                                onClick={() => handleOpenVirtualTour(ast)}
                                className="bg-[#00a1df] hover:bg-blue-500 text-white px-3 py-2 rounded-full text-[11px] font-black uppercase flex items-center gap-2 justify-center transition-all shadow-[0_0_15px_rgba(0,161,223,0.5)] w-full"
                              >
                                <Sparkles size={14}/> Virtual Tour
                              </button>

                              {ast.model_urn && (
                                <button 
                                  // 🎯 تمرير بيانات العدادات والورك أوردر من الأصل (ast)
                                  onClick={() => handleOpenViewer(ast.model_urn, ast.assetnum, ast.bim_id, ast.description, ast.status, ast.meters, ast.stats, ast.asset_id)}
                                  className="bg-white text-[#1a3353] px-3 py-2 rounded-full text-[11px] font-black uppercase flex items-center gap-2 justify-center transition-all w-full shadow-lg"
                                >
                                  <Box size={14}/> Digital Twin (BIM)
                                </button>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </>
                )}
              </MapContainer>
            </div>
          </div>

          </div>
        </div>
      )}
      </div>
      
      {/* 🎯 Custom CSS للستايل الجديد (الدار) */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* إخفاء ديفولت الليفلت بوب اب تماماً */
          .glass-popup .leaflet-popup-content-wrapper {
            background: rgba(20, 20, 20, 0.8) !important;
            backdrop-filter: blur(12px) !important;
            border: 1px solid rgba(255,255,255,0.2) !important;
            box-shadow: 0 10px 30px rgba(0,0,0,0.5) !important;
            border-radius: 16px !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            padding: 0 !important;
          }
          .glass-popup .leaflet-popup-tip-container { display: none !important; }
          .glass-popup .leaflet-popup-content { margin: 0 !important; width: auto !important; }
          
          /* Aldar Style Macro Callouts (النسخة المضيئة) */
          .aldar-callout-container { position: relative; width: 160px; height: 120px; display: flex; flex-direction: column; align-items: center; z-index: 1000 !important; }
          .aldar-callout-box { 
            background: #1a1a1a !important; 
            border: 1px solid #ff6600 !important; 
            color: white; text-align: center; width: 100%; cursor: pointer; 
            box-shadow: 0 0 15px rgba(255, 102, 0, 0.4) !important; 
            border-radius: 4px; transition: all 0.3s; 
            overflow: hidden;
          }
          .aldar-callout-container:hover .aldar-callout-box { transform: scale(1.05); border-color: #fff !important; }
          .aldar-callout-title { padding: 10px; font-weight: 900; font-size: 11px; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.5px; }
          .aldar-callout-explore { background: #ff6600; color: white; font-size: 9px; font-weight: 900; padding: 4px; letter-spacing: 1.5px; }
          .aldar-callout-line { width: 2px; height: 30px; background: #ff6600; box-shadow: 0 0 10px #ff6600; margin-top: 2px; }
          .aldar-callout-dot { width: 10px; height: 10px; background: #fff; border-radius: 50%; box-shadow: 0 0 15px #fff; margin-top: -5px; }

          /* إزاحة أدوات الرسم (Draw Controls) للأسفل لمنع التداخل مع الهيدر */
          .leaflet-top.leaflet-left { margin-top: 80px !important; margin-left: 10px !important; }
          .leaflet-control-zoom { display: none !important; }
          .custom-map-tiles .leaflet-layer { filter: contrast(1.1) brightness(0.9) saturate(0.9); }
          .leaflet-image-layer {
              z-index: 999 !important; /* خليها أعلى من أي حاجة في الخريطة */
            }
        `
      }} />

    </div>
  );
};

// --- Reusable Shared Components ---

const LookupField = ({ label, value, name, desc, onSearch, onChange, isRTL }) => (
  <div className={`flex items-center gap-4 group `}>
    <label className={`w-48 font-bold text-gray-500 uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>{label}</label>
    <div className={`flex items-center gap-2 border-b border-gray-300 group-focus-within:border-blue-500 transition-colors w-[450px] `}>
      <input name={name} value={value || ''} onChange={onChange} className={`w-24 outline-none font-bold uppercase bg-transparent text-blue-900 `} />
      <ArrowRight size={14} className={`text-blue-700 cursor-pointer hover:scale-110 ${isRTL ? 'rotate-180' : ''}`} onClick={onSearch} />
      <span className={`flex-1 text-gray-400 italic truncate border-gray-100 uppercase text-[10px] ${isRTL ? 'border-r pr-3 text-right' : 'border-l pl-3 text-left'}`}>{desc}</span>
      <List size={14} className="text-blue-700 cursor-pointer" />
    </div>
  </div>
);

const SearchField = ({ label, value, name, isRequired, onChange, onSearch, isRTL }) => (
  <div className={`flex items-center gap-4 `}>
    <label className={`w-48 font-bold uppercase tracking-tighter ${isRTL ? 'text-left' : 'text-right'} ${isRequired ? 'text-red-700' : 'text-gray-500'}`}>
      {isRequired && !isRTL && <span className="mr-1 font-black text-red-500">+</span>}
      {label}
      {isRequired && isRTL && <span className="ml-1 font-black text-red-500">+</span>}
    </label>
    <div className={`flex items-center gap-2 border-b border-gray-300 w-[450px] `}>
      <input name={name} value={value || ''} onChange={onChange} className={`flex-1 outline-none font-bold bg-transparent text-blue-900 uppercase `} />
      <Search size={14} className="text-blue-700 cursor-pointer hover:text-blue-900 transition-colors" onClick={onSearch} />
    </div>
  </div>
);

const DetailField = ({ label, value, isRequired, hasSearch, hasList, onChange, onSearch, isRTL }) => (
  <div className="flex flex-col border-b border-gray-300 focus-within:border-blue-500 transition-all">
    <label className={`text-[10px] font-black uppercase tracking-tighter ${isRequired ? 'text-red-700' : 'text-gray-400'} `}>
      {isRequired && !isRTL && <span className="mr-1">+</span>}
      {label}
      {isRequired && isRTL && <span className="ml-1">+</span>}
    </label>
    <div className={`flex items-center justify-between py-1 min-h-[24px] `}>
       <input className={`font-bold uppercase outline-none bg-transparent flex-1 text-blue-900 text-xs `} value={value || ''} 
              onChange={(e) => onChange && onChange(e.target.value)} />
       <div className="flex gap-2">
         {hasSearch && <Search size={13} className="text-blue-800 cursor-pointer hover:scale-110" onClick={onSearch}/>}
         {hasList && <List size={13} className="text-blue-800 cursor-pointer hover:scale-110"/>}
       </div>
    </div>
  </div>
);

export default OrganizationDetail;